import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Save, X, Camera, Video, Zap,
  ClipboardCopy, Check, Sparkles, BadgeCheck,
  Send, AlertTriangle, ChevronDown, ChevronUp, Wrench,
} from "lucide-react";
import { copyToClipboard } from "@/lib/waMessages";
import { processVideoFile } from "@/lib/videoProcessing";

const MODALITY_LABEL = {
  WRITTEN: "Escrito", VISUAL: "Visual", BOTH: "Escrito + Visual", NONE: "Sin diagnóstico",
};
const ORIGIN_LABEL = {
  MANUAL: "Manual",
  AI: "Asistente IA + confirmado por técnico",
  VISUAL: "Evidencia visual",
};

// Plantillas rápidas: el técnico toca y la app inserta el texto (append si ya hay contenido)
const QUICK_TEMPLATES = [
  { key: "drift", label: "Drift de joystick", text: "Se detecta drift en el joystick: el stick se mueve solo sin ser tocado. Se requiere reemplazo del módulo analógico. Se verificó el comportamiento en modo diagnóstico y persiste tras calibración." },
  { key: "no-charge", label: "No carga / batería", text: "El equipo no toma carga o la batería no retiene: se probó con cargador y cable originales sin resultado. Se requiere revisión del pin de carga / reemplazo de batería." },
  { key: "buttons", label: "Botones no responden", text: "Uno o más botones no responden o responden intermitentemente. Se recomienda limpieza interna y, si persiste, reemplazo de los contactos de goma / membrana." },
  { key: "lens", label: "Lente láser sucio", text: "El lector no reconoce discos o los expulsa. Se aprecia lente sucio/desalineado. Se recomienda limpieza del lente y prueba con varios discos. Si persiste, reemplazo del lector." },
  { key: "screen", label: "Pantalla dañada", text: "Se observa daño físico en la pantalla (rayaduras/cristal fisurado/manchas). Se requiere reemplazo del panel/LCD. Se realizarán pruebas de funcionamiento tras el cambio." },
  { key: "overheat", label: "Sobrecalentamiento", text: "El equipo se apaga por temperatura o se percibe caliente rápidamente. Se recomienda mantenimiento interno: limpieza de disipador, cambio de pasta térmica y verificación de ventiladores." },
];

/** Big, mobile-friendly button */
function BigBtn({ onClick, children, variant = "secondary", disabled, testid, className = "" }) {
  const styles = {
    primary: "bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white",
    success: "bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-400 hover:to-green-500 text-white",
    secondary: "bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200",
    danger: "bg-white text-red-600 border border-red-200 hover:bg-red-50",
  };
  return (
    <button
      type="button" onClick={onClick} disabled={disabled} data-testid={testid}
      className={`min-h-[48px] px-4 py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed ${styles[variant]} ${className}`}
    >
      {children}
    </button>
  );
}

export default function DiagnosisSection({ order, scopeDev, deviceId, isSecondaryView, user, photos, onSaved, diagnosisPreset, onUploadMedia, uploading }) {
  const cameraInputRef = useRef(null);
  const [captureBusy, setCaptureBusy] = useState(false);
  const [captureStatus, setCaptureStatus] = useState("");  // Texto visible durante el procesamiento (ej. "Convirtiendo video… 45%")

  // El "device activo" es scopeDev si viene, sino la orden misma (compatibilidad).
  const dev = scopeDev || order;

  // Local editing state — only committed on Save.
  const canEdit =
    ["admin", "supervisor"].includes(user.role) ||
    user.role === "technician";

  // === AI inline panel state ===
  const isFullEdit = ["admin", "supervisor"].includes(user.role);
  const canUseAI = isFullEdit || user.role === "technician";
  const [aiOpen, setAiOpen] = useState(false);
  const [aiSymptom, setAiSymptom] = useState(dev.problem_reported || "");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiDraftLoading, setAiDraftLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [aiError, setAiError] = useState("");

  // Ciclo actual: si estamos en garantía, tomamos el diagnóstico del último ciclo (no toca el original)
  // La garantía es a nivel de la ORDEN completa (por ahora); sólo aplica en la vista primaria.
  const currentCycle = order.current_cycle || "original";
  const isWarrantyCycle = !isSecondaryView && String(currentCycle).startsWith("warranty_");
  const cycles = order.warranty_cycles || [];
  const activeCycle = isWarrantyCycle && cycles.length > 0 ? cycles[cycles.length - 1] : null;

  const officialModality = (activeCycle?.diagnosis_modality) || dev.diagnosis_modality || "NONE";
  const officialText = (activeCycle?.diagnosis) ?? (dev.diagnosis || "");
  const officialMediaIds = (activeCycle?.diagnosis_media_ids) || dev.diagnosis_media_ids || [];
  const officialOrigin = activeCycle ? activeCycle.diagnosis_origin : dev.diagnosis_origin;

  // Diagnóstico original (siempre visible cuando estamos en ciclo de garantía)
  const originalModality = dev.diagnosis_modality || "NONE";
  const originalText = dev.diagnosis || "";
  const originalMediaIds = dev.diagnosis_media_ids || [];

  const [draftText, setDraftText] = useState(officialText);
  const [draftMediaIds, setDraftMediaIds] = useState(officialMediaIds);
  const [draftOrigin, setDraftOrigin] = useState(officialOrigin);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(null);

  // When the order refreshes from server, reset local state (unless we have a pending AI preset).
  useEffect(() => {
    if (diagnosisPreset && diagnosisPreset.seq && diagnosisPreset.text != null) {
      setDraftText(diagnosisPreset.text);
      setDraftOrigin("AI");
    }
    // eslint-disable-next-line
  }, [diagnosisPreset && diagnosisPreset.seq]);

  useEffect(() => {
    setDraftText(officialText);
    setDraftMediaIds(officialMediaIds);
    setDraftOrigin(officialOrigin);
    // eslint-disable-next-line
  }, [order.id, deviceId, officialText, JSON.stringify(officialMediaIds), officialOrigin]);

  // Mantener el input de síntoma sincronizado con el problema reportado
  useEffect(() => {
    setAiSymptom(dev.problem_reported || "");
  }, [order.id, deviceId, dev.problem_reported]);

  async function askAI() {
    const symptom = (aiSymptom || "").trim();
    if (symptom.length < 3) {
      setAiError("No hay síntoma disponible. Escribí algo o cargá el problema reportado en la orden.");
      return;
    }
    setAiLoading(true);
    setAiError("");
    setAiResult(null);
    try {
      const r = await api.post(`/orders/${order.id}/ai-assist`, { symptom });
      setAiResult(r.data);
    } catch (e) {
      setAiError(formatApiError(e));
    } finally {
      setAiLoading(false);
    }
  }

  async function useAIAsDiagnosis() {
    const symptom = (aiSymptom || "").trim();
    if (symptom.length < 3) return;
    if (!canUseAI) {
      toast.error("Solo técnicos o supervisión pueden convertir sugerencias en diagnóstico");
      return;
    }
    setAiDraftLoading(true);
    setAiError("");
    try {
      const r = await api.post(`/orders/${order.id}/ai-diagnosis-draft`, {
        symptom,
        ai_answer: aiResult?.answer || "",
      });
      const draft = (r.data?.draft || "").trim();
      if (!draft) {
        setAiError("La IA no generó un borrador. Reformulá el síntoma.");
        return;
      }
      setDraftText(draft);
      setDraftOrigin("AI");
      toast.success("Borrador cargado. Revisá, editá si querés y guardá.");
      setAiOpen(false);
      // Scroll to textarea
      setTimeout(() => {
        document.querySelector('[data-testid="diagnosis-text-input"]')
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 200);
    } catch (e) {
      setAiError(formatApiError(e));
    } finally {
      setAiDraftLoading(false);
    }
  }

  // Dirty check
  const dirty = useMemo(() => {
    if ((draftText || "") !== (officialText || "")) return true;
    if (draftMediaIds.length !== officialMediaIds.length) return true;
    for (let i = 0; i < draftMediaIds.length; i++) {
      if (draftMediaIds[i] !== officialMediaIds[i]) return true;
    }
    return false;
  }, [draftText, draftMediaIds, officialText, officialMediaIds]);

  // Modalidad inferida automáticamente a partir del contenido del draft
  const inferredModality = useMemo(() => {
    const hasText = !!(draftText || "").trim();
    const hasMedia = (draftMediaIds || []).length > 0;
    if (hasText && hasMedia) return "BOTH";
    if (hasText) return "WRITTEN";
    if (hasMedia) return "VISUAL";
    return "NONE";
  }, [draftText, draftMediaIds]);

  const toggleMedia = (id) => {
    setDraftMediaIds((prev) => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const canSave = () => inferredModality !== "NONE";

  async function save() {
    if (!canSave()) {
      return toast.error("Escribí el diagnóstico o adjuntá al menos una foto/video");
    }
    setSaving(true);
    try {
      const modality = inferredModality;
      const body = {
        modality,
        text: modality === "VISUAL" ? "" : draftText.trim(),
        media_ids: modality === "WRITTEN" ? [] : draftMediaIds,
        origin: draftOrigin || (modality === "VISUAL" ? "VISUAL" : "MANUAL"),
      };
      // Multi-equipo: si estamos en la vista de un equipo secundario, scope al device
      if (isSecondaryView && deviceId) body.device_id = deviceId;
      await api.patch(`/orders/${order.id}/diagnosis`, body);
      toast.success("Diagnóstico oficial guardado");
      onSaved && onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  }

  function cancel() {
    setDraftText(officialText);
    setDraftMediaIds(officialMediaIds);
    setDraftOrigin(officialOrigin);
    toast.info("Cambios descartados");
  }

  // === Plantilla rápida: inserta o reemplaza el texto según el estado ===
  function insertTemplate(tpl) {
    setDraftText((prev) => {
      const cur = (prev || "").trim();
      if (!cur) return tpl.text;
      // Si ya existe la misma plantilla, no la dupliques
      if (cur.includes(tpl.text)) return cur;
      return `${cur}\n\n${tpl.text}`;
    });
    setDraftOrigin("MANUAL");
    toast.success(`Plantilla "${tpl.label}" insertada`);
  }

  // === Cámara nativa: dispara app de cámara en móvil (input capture) ===
  function openCamera() {
    if (cameraInputRef.current) cameraInputRef.current.click();
  }

  async function handleCaptureFiles(e) {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    if (captureBusy) return;  // evita doble envío mientras procesa
    setCaptureBusy(true);
    setCaptureStatus("Preparando archivos…");
    try {
      const newIds = [];
      for (const file of files) {
        const isVideo = (file.type || "").startsWith("video/");

        // Rama FOTO: comportamiento original — lectura directa a dataURL.
        if (!isVideo) {
          const maxMb = 20;
          if (file.size > maxMb * 1024 * 1024) {
            toast.error(`${file.name}: excede ${maxMb}MB`);
            continue;
          }
          setCaptureStatus(`Subiendo ${file.name}…`);
          const dataUrl = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });
          const res = await api.post(`/orders/${order.id}/photos`, {
            order_id: order.id,
            device_id: deviceId || order.id,
            category: "DIAGNOSIS",
            data: dataUrl,
            caption: "",
            visible_to_client: true,
            media_type: "photo",
            mime_type: file.type || "image/jpeg",
            duration: null,
          });
          if (res?.data?.id) newIds.push(res.data.id);
          continue;
        }

        // Rama VIDEO: pasa por el pipeline compartido (valida duración 20s,
        // acepta hasta MAX_ORIGINAL_MB=120MB antes del transcodeo, transcodifica
        // a H.264/MP4 720p cuando corresponde, genera miniatura, cap final 11MB).
        let processed;
        try {
          processed = await processVideoFile(file, (msg) => setCaptureStatus(msg));
        } catch (err) {
          toast.error(err?.message || "No se pudo procesar el video");
          continue;
        }

        setCaptureStatus(`Subiendo ${file.name}…`);
        const res = await api.post(`/orders/${order.id}/photos`, {
          order_id: order.id,
          device_id: deviceId || order.id,
          category: "DIAGNOSIS",
          data: processed.dataUrl,
          caption: "",
          visible_to_client: true,
          media_type: "video",
          mime_type: processed.mimeType || "video/mp4",
          duration: Math.round(processed.duration),
          thumbnail: processed.thumbnail || null,
        });
        if (res?.data?.id) newIds.push(res.data.id);
      }

      if (newIds.length > 0) {
        setDraftMediaIds((prev) => [...prev, ...newIds]);
        toast.success(`${newIds.length} archivo${newIds.length > 1 ? "s" : ""} agregado${newIds.length > 1 ? "s" : ""} al diagnóstico`);
        onSaved && onSaved(); // refresca lista de fotos
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCaptureBusy(false);
      setCaptureStatus("");
      if (cameraInputRef.current) cameraInputRef.current.value = "";
    }
  }

  // Firma actual: preferimos la del ciclo activo si aplica, sino la del device activo
  const signature = {
    name: activeCycle?.diagnosis_signed_by_name || dev.diagnosis_signed_by_name,
    role: activeCycle?.diagnosis_signed_by_role || dev.diagnosis_signed_by_role,
    at: activeCycle?.diagnosis_signed_at || dev.diagnosis_signed_at,
  };

  function clearDiagnosis() {
    if (!confirm("¿Eliminar el diagnóstico oficial de la orden?")) return;
    (async () => {
      try {
        const body = { modality: "NONE", text: "", media_ids: [] };
        if (isSecondaryView && deviceId) body.device_id = deviceId;
        await api.patch(`/orders/${order.id}/diagnosis`, body);
        toast.success("Diagnóstico eliminado");
        onSaved && onSaved();
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }

  async function copyDiagnosis() {
    const eq = [dev.brand, dev.model].filter(Boolean).join(" ") ||
      [dev.category?.name, dev.subcategory?.name].filter(Boolean).join(" · ");
    const lines = [
      `🔧 *Diagnóstico - Punto Play / PuntoCell*`,
      "",
      `📋 Orden: *${order.order_number}*`,
      eq && `📱 Equipo: ${eq}`,
    ].filter(Boolean);
    if (officialModality === "WRITTEN") {
      lines.push(""); lines.push("🔍 *Diagnóstico:*"); lines.push(officialText);
    } else if (officialModality === "VISUAL") {
      lines.push(""); lines.push("📸 *Diagnóstico:*");
      lines.push("Se adjunta evidencia visual del diagnóstico de la reparación.");
    } else if (officialModality === "BOTH") {
      lines.push(""); lines.push("🔍 *Diagnóstico:*"); lines.push(officialText);
      lines.push(""); lines.push("📸 Se cuenta además con evidencia visual del diagnóstico.");
    } else {
      return toast.error("No hay diagnóstico oficial para copiar");
    }
    const ok = await copyToClipboard(lines.join("\n"));
    if (ok) {
      setCopied(true); toast.success("Copiado ✓");
      setTimeout(() => setCopied(false), 1600);
    } else toast.error("No se pudo copiar");
  }

  const hasOfficial = officialModality !== "NONE";

  return (
    <div className="card-base p-5" data-testid="diagnosis-section">
      {isWarrantyCycle && (
        <div
          className="mb-3 rounded-xl border-2 border-indigo-300 bg-indigo-50 p-3 text-sm text-indigo-900"
          data-testid="diagnosis-warranty-banner"
        >
          <div className="font-semibold flex items-center gap-2">
            🛡️ Diagnóstico de <span>Garantía #{cycles.length}</span>
          </div>
          <div className="text-xs mt-1 text-indigo-800">
            El diagnóstico original permanece intacto. Este editor guarda el nuevo diagnóstico del ciclo de garantía actual.
          </div>
          {originalModality !== "NONE" && (
            <details className="mt-2">
              <summary className="cursor-pointer text-xs font-mono uppercase tracking-wider text-indigo-700 hover:text-indigo-900">
                Ver diagnóstico original (referencia)
              </summary>
              <div className="mt-2 rounded-lg bg-white border border-indigo-200 p-2 text-xs text-slate-700 whitespace-pre-wrap" data-testid="diagnosis-original-reference">
                {originalText || <span className="italic text-slate-500">(sin texto — solo visual)</span>}
                {originalMediaIds.length > 0 && (
                  <div className="mt-1 text-slate-500 italic">Incluye {originalMediaIds.length} archivo{originalMediaIds.length > 1 ? "s" : ""} de evidencia.</div>
                )}
              </div>
            </details>
          )}
        </div>
      )}

      {/* Header */}
      <div className="flex justify-between items-start gap-2 mb-4 flex-wrap">
        <div>
          <h3 className="font-heading font-extrabold text-xl text-slate-800">Diagnóstico</h3>
          {hasOfficial ? (
            <div className="text-[11px] text-slate-500 font-mono mt-0.5" data-testid="diagnosis-status">
              Guardado · <span className="text-emerald-700 font-semibold">{MODALITY_LABEL[officialModality]}</span>
              {officialOrigin && <> · Origen: {ORIGIN_LABEL[officialOrigin] || officialOrigin}</>}
            </div>
          ) : (
            <div className="text-[11px] text-slate-500 mt-0.5" data-testid="diagnosis-status">Sin diagnóstico oficial todavía</div>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          {canUseAI && (
            <BigBtn variant="primary" onClick={() => setAiOpen((v) => !v)} testid="diagnosis-open-ai"
              className="py-2 min-h-[44px] text-sm bg-gradient-to-r from-violet-600 to-blue-600 hover:from-violet-500 hover:to-blue-500">
              <Sparkles size={16} /> {aiOpen ? "Cerrar Asistente IA" : "Asistente IA"}
              {aiOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </BigBtn>
          )}
          <BigBtn variant="secondary" onClick={copyDiagnosis} disabled={!hasOfficial} testid="diagnosis-copy" className="py-2 min-h-[44px] text-xs">
            {copied ? <><Check size={14} /> Copiado</> : <><ClipboardCopy size={14} /> Copiar</>}
          </BigBtn>
        </div>
      </div>

      {/* Panel IA inline — se expande justo debajo del botón, sin modal */}
      {aiOpen && canUseAI && (
        <div
          className="mb-4 rounded-2xl border-2 border-violet-200 bg-gradient-to-br from-violet-50 via-blue-50 to-white p-4 shadow-sm"
          data-testid="diagnosis-ai-panel"
        >
          <div className="flex items-center justify-between gap-2 mb-3">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-blue-600 flex items-center justify-center text-white shadow flex-shrink-0">
                <Sparkles size={18} />
              </div>
              <div className="min-w-0">
                <div className="font-heading font-extrabold text-slate-900 text-base leading-tight">Asistente IA</div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Diagnóstico técnico · Gemini</div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setAiOpen(false)}
              className="p-1.5 rounded-lg hover:bg-white/60 text-slate-500"
              data-testid="diagnosis-ai-close"
              aria-label="Cerrar Asistente IA"
            >
              <X size={18} />
            </button>
          </div>

          {/* Contexto del equipo + problema reportado (auto-cargados) */}
          <div className="rounded-xl bg-white border border-slate-200 p-3 mb-3 text-xs">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-1">
              Contexto que usará la IA
            </div>
            <div className="text-slate-800 mb-1">
              <b>Equipo:</b>{" "}
              {[dev.category?.name, dev.subcategory?.name, dev.brand, dev.model].filter(Boolean).join(" · ") || "Sin datos"}
            </div>
            <div className="text-slate-800">
              <b>Problema reportado por el cliente:</b>{" "}
              {dev.problem_reported ? (
                <span className="text-slate-700">{dev.problem_reported}</span>
              ) : (
                <span className="text-slate-400 italic">Sin descripción cargada aún</span>
              )}
            </div>
          </div>

          {/* Textarea con síntoma editable (pre-cargado con problem_reported) */}
          <label className="block text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-1.5">
            Síntoma para la IA (podés editarlo si querés)
          </label>
          <textarea
            value={aiSymptom}
            onChange={(e) => setAiSymptom(e.target.value)}
            placeholder="Describí el síntoma o dejá el problema reportado del cliente…"
            className="input-base min-h-[80px] text-sm mb-2"
            maxLength={2000}
            disabled={aiLoading}
            data-testid="diagnosis-ai-symptom"
          />

          <div className="flex gap-2 flex-wrap mb-3">
            <BigBtn
              variant="primary"
              onClick={askAI}
              disabled={aiLoading || (aiSymptom || "").trim().length < 3}
              testid="diagnosis-ai-ask"
              className="flex-1 min-w-[160px] bg-gradient-to-r from-violet-600 to-blue-600 hover:from-violet-500 hover:to-blue-500"
            >
              {aiLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                  Consultando IA…
                </>
              ) : (
                <><Send size={16} /> {aiResult ? "Volver a consultar" : "Consultar IA"}</>
              )}
            </BigBtn>
          </div>

          {aiError && (
            <div className="rounded-xl bg-red-50 border border-red-200 p-3 text-sm text-red-700 mb-3" data-testid="diagnosis-ai-error">
              {aiError}
            </div>
          )}

          {aiResult && (
            <div className="space-y-3" data-testid="diagnosis-ai-result">
              <div className="rounded-xl border border-slate-200 bg-white p-4 ai-answer text-sm">
                <AIAnswer text={aiResult.answer} />
              </div>
              <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800 flex items-start gap-2">
                <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
                <span>{aiResult.disclaimer}</span>
              </div>
              <BigBtn
                variant="success"
                onClick={useAIAsDiagnosis}
                disabled={aiDraftLoading}
                testid="diagnosis-ai-use"
                className="w-full py-3"
              >
                {aiDraftLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    Redactando borrador…
                  </>
                ) : (
                  <><Wrench size={16} /> Copiar como diagnóstico oficial</>
                )}
              </BigBtn>
              <div className="text-[10px] text-slate-400 font-mono text-right">
                Modelo: {aiResult.model}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Vista de solo lectura para roles sin permiso */}
      {hasOfficial && !canEdit && (
        <div className="mb-4 space-y-3">
          {officialText && (
            <div className="rounded-xl bg-blue-50 border border-blue-200 p-3 text-sm text-slate-800 whitespace-pre-wrap" data-testid="diagnosis-official-text">
              {officialText}
            </div>
          )}
          {officialMediaIds.length > 0 && (
            <MediaGrid photos={photos} selectedIds={officialMediaIds} readonly />
          )}
          {signature.name && (
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-800 flex items-center gap-2" data-testid="diagnosis-signature-readonly">
              <BadgeCheck size={14} className="text-emerald-600" />
              <span>Firmado por <b>{signature.name}</b> {signature.role && <span className="text-emerald-600">({signature.role === "admin" ? "Administrador" : signature.role === "supervisor" ? "Supervisora" : signature.role === "technician" ? "Técnico" : "Recepción / Vendedor"})</span>} · {signature.at ? new Date(signature.at).toLocaleString("es-AR") : ""}</span>
            </div>
          )}
        </div>
      )}

      {/* Editor unificado (siempre visible para roles con permiso) */}
      {canEdit && (
        <>
          {/* Input hidden para cámara nativa (dispara app cámara en móvil).
             SOLO imagen y video (sin multiple) + capture="environment" → en
             Android/Samsung abre directamente la cámara y permite elegir
             Foto o Video (si el OEM lo soporta). */}
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*,video/*"
            capture="environment"
            onChange={handleCaptureFiles}
            className="hidden"
            data-testid="diagnosis-camera-input"
          />

          {/* Ayuda breve */}
          <div className="rounded-xl bg-blue-50 border border-blue-100 p-3 text-xs text-blue-900 mb-4" data-testid="diagnosis-help">
            <b>Cómo funciona:</b> escribí el diagnóstico, adjuntá fotos/videos (o ambas cosas). Podés pedirle al <b>Asistente IA</b> un borrador, usar una <b>plantilla rápida</b>, o disparar la <b>cámara</b> directo. El diagnóstico oficial es el que ve el cliente en el portal público.
          </div>

          {/* Plantillas rápidas */}
          <div className="mb-4" data-testid="diagnosis-quick-templates">
            <div className="flex items-center gap-1.5 mb-2">
              <Zap size={14} className="text-amber-600" />
              <label className="text-xs font-mono uppercase tracking-wider text-slate-500">
                Plantillas rápidas
              </label>
            </div>
            <div className="flex flex-wrap gap-2">
              {QUICK_TEMPLATES.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => insertTemplate(t)}
                  data-testid={`diagnosis-template-${t.key}`}
                  className="text-xs font-semibold px-3 py-1.5 rounded-full bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-200 transition-colors"
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Editor de texto — SIEMPRE visible */}
          <div className="mb-4">
            <label className="block text-xs font-mono uppercase tracking-wider text-slate-500 mb-1.5">
              1) Diagnóstico escrito
              {draftOrigin === "AI" && (draftText || "").trim() && (
                <span className="text-violet-600 normal-case font-semibold ml-1">· Sugerido por IA (revisá antes de guardar)</span>
              )}
            </label>
            <textarea
              value={draftText}
              onChange={(e) => { setDraftText(e.target.value); }}
              placeholder="Escribí el diagnóstico, elegí una plantilla o pedile un borrador al Asistente IA…"
              className="input-base min-h-[140px] text-sm"
              data-testid="diagnosis-text-input"
            />
            <div className="text-[11px] text-slate-500 mt-1">Podés dejar este campo vacío si el diagnóstico es solo visual.</div>
          </div>

          {/* Grilla de fotos/videos — SIEMPRE visible */}
          <div className="mb-4">
            <div className="flex justify-between items-center mb-2 flex-wrap gap-2">
              <label className="text-xs font-mono uppercase tracking-wider text-slate-500">
                2) Fotos y videos del diagnóstico
                {draftMediaIds.length > 0 && <span className="text-emerald-700 ml-1">· {draftMediaIds.length} seleccionado{draftMediaIds.length > 1 ? "s" : ""}</span>}
              </label>
              <div className="flex gap-2">
                <BigBtn variant="primary" onClick={openCamera} disabled={captureBusy || uploading} testid="diagnosis-camera-btn" className="py-1.5 min-h-[36px] text-xs">
                  <Camera size={14} /> {captureBusy ? "Procesando…" : "Cámara"}
                </BigBtn>
                {onUploadMedia && (
                  <BigBtn variant="secondary" onClick={onUploadMedia} disabled={uploading || captureBusy} testid="diagnosis-add-media" className="py-1.5 min-h-[36px] text-xs">
                    <Video size={14} /> {uploading ? "Subiendo…" : "Desde galería"}
                  </BigBtn>
                )}
              </div>
            </div>
            {captureBusy && captureStatus && (
              <div
                className="mb-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-900 flex items-center gap-2"
                data-testid="diagnosis-capture-status"
              >
                <div className="w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                <span>{captureStatus}</span>
              </div>
            )}
            {photos.length === 0 ? (
              <div className="text-center text-slate-500 text-xs py-6 border border-dashed border-slate-200 rounded-xl" data-testid="diagnosis-no-media">
                Aún no hay fotos ni videos. Tocá <b>"Cámara"</b> para tomar una directamente o <b>"Desde galería"</b> para subir una existente.
              </div>
            ) : (
              <>
                <div className="text-[11px] text-slate-500 mb-1.5">Tocá una foto/video para incluirla en el diagnóstico oficial.</div>
                <MediaGrid photos={photos} selectedIds={draftMediaIds} onToggle={toggleMedia} />
              </>
            )}
          </div>

          {/* Indicador de modalidad inferida + firma pendiente */}
          <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 mb-3 text-xs flex items-center justify-between gap-2" data-testid="diagnosis-inferred-mode">
            <div className="flex items-center gap-2">
              <span className="text-slate-600">Al guardar quedará como:</span>
              <span className="font-mono font-bold text-slate-800">
                {inferredModality === "NONE" ? "— (nada aún)" : MODALITY_LABEL[inferredModality]}
              </span>
            </div>
            {inferredModality !== "NONE" && (
              <div className="flex items-center gap-1 text-emerald-700">
                <BadgeCheck size={13} />
                <span>Firmará <b>{user.name}</b></span>
              </div>
            )}
          </div>

          {/* Firma anterior si ya está guardado y no dirty */}
          {signature.name && !dirty && (
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 mb-3 text-xs text-emerald-800 flex items-center gap-2" data-testid="diagnosis-signature">
              <BadgeCheck size={14} className="text-emerald-600" />
              <span>Firmado por <b>{signature.name}</b> · {signature.at ? new Date(signature.at).toLocaleString("es-AR") : ""}</span>
            </div>
          )}

          {/* Acciones */}
          <div className="flex gap-2 flex-wrap pt-3 border-t border-slate-100">
            <BigBtn variant="success" onClick={save} disabled={!dirty || saving || !canSave()} testid="diagnosis-save" className="flex-1 min-w-[140px]">
              <Save size={16} /> {saving ? "Guardando…" : "Guardar diagnóstico"}
            </BigBtn>
            {dirty && (
              <BigBtn variant="secondary" onClick={cancel} testid="diagnosis-cancel">
                <X size={16} /> Cancelar
              </BigBtn>
            )}
            {hasOfficial && !dirty && (
              <BigBtn variant="danger" onClick={clearDiagnosis} testid="diagnosis-clear">
                <X size={16} /> Quitar oficial
              </BigBtn>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function MediaGrid({ photos, selectedIds, onToggle, readonly = false }) {
  const items = readonly ? photos.filter(p => selectedIds.includes(p.id)) : photos;
  const byId = Object.fromEntries(photos.map(p => [p.id, p]));
  const orderedItems = readonly ? selectedIds.map(id => byId[id]).filter(Boolean) : items;
  return (
    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
      {orderedItems.map((p) => {
        const selected = selectedIds.includes(p.id);
        const isVideo = p.media_type === "video";
        const poster = isVideo ? (p.thumbnail || null) : p.data;
        return (
          <button
            type="button" key={p.id} onClick={() => !readonly && onToggle && onToggle(p.id)} disabled={readonly}
            data-testid={`diagnosis-media-${p.id}`}
            className={`relative aspect-square rounded-xl overflow-hidden border-2 transition-all ${
              selected ? "border-emerald-500 ring-2 ring-emerald-300" : "border-slate-200"
            } ${readonly ? "cursor-default" : "hover:border-blue-400"}`}
          >
            {poster ? (
              <img src={poster} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-slate-100 text-slate-500 text-xs">Video</div>
            )}
            {isVideo && (
              <div className="absolute top-1.5 left-1.5 bg-slate-900/80 text-white text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full font-mono flex items-center gap-1">
                <Video size={9} /> Video
              </div>
            )}
            {selected && (
              <div className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-emerald-500 text-white flex items-center justify-center shadow">
                <Check size={14} />
              </div>
            )}
            <div className="absolute bottom-0 left-0 right-0 bg-slate-900/60 text-white text-[9px] font-mono px-1.5 py-0.5 pointer-events-none">
              {p.category}
            </div>
          </button>
        );
      })}
      {readonly && orderedItems.length === 0 && (
        <div className="col-span-full text-center text-slate-400 text-xs py-4">Sin evidencia</div>
      )}
    </div>
  );
}


// Minimal safe markdown renderer for AI responses.
function AIAnswer({ text }) {
  if (!text) return null;
  const lines = text.split(/\r?\n/);
  const blocks = [];
  let currentList = null;
  const flushList = () => {
    if (currentList) {
      blocks.push(currentList);
      currentList = null;
    }
  };
  lines.forEach((raw, i) => {
    const line = raw.trim();
    if (!line) { flushList(); return; }
    const h2 = line.match(/^##\s+(.+)/);
    const li = line.match(/^[-*]\s+(.+)/);
    if (h2) {
      flushList();
      blocks.push(<h2 key={`h-${i}`}>{renderInline(h2[1])}</h2>);
    } else if (li) {
      if (!currentList) currentList = <ul key={`ul-${i}`}>{[]}</ul>;
      const existingChildren = currentList.props.children;
      currentList = (
        <ul key={currentList.key}>
          {existingChildren}
          <li key={`li-${i}`}>{renderInline(li[1])}</li>
        </ul>
      );
    } else {
      flushList();
      blocks.push(<p key={`p-${i}`}>{renderInline(line)}</p>);
    }
  });
  flushList();
  return <>{blocks}</>;
}

function renderInline(text) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    const m = p.match(/^\*\*([^*]+)\*\*$/);
    if (m) return <strong key={i}>{m[1]}</strong>;
    return <span key={i}>{p}</span>;
  });
}
